/**
 * useHexInteraction Hook
 * 
 * Handles hex click interactions, player movement, and center changes
 */

import { useCallback } from 'react';
import { AxialCoord, TerrainMap, TerrainType } from '../types/terrain';
import { hexDistance, hexToPixel, pixelToHex, isInBounds } from '../utils/hexMath';
import { usePlayer } from '../contexts/PlayerContext';
import { getTravelTimeSetting } from '../components/SettingsPanel';
import { FEATURES } from '../config/features';

interface UseHexInteractionProps {
  map: TerrainMap;
  center: AxialCoord;
  zoom: number;
  isDragging: boolean;
  hasDragged: boolean;
  offset: { x: number; y: number };
  canvasSize: { width: number; height: number };
  hexSize: number;
  onCenterChange?: (coord: AxialCoord) => void;
  setZoom: (zoom: number) => void;
  hasUnresolvedMonster?: boolean;  // Block movement if there's an unresolved monster encounter
  isInCombat?: boolean;  // Block movement if player is in combat
  onPlayerMarkerClick?: () => void; // Called when player marker is clicked
  onDistantHexClick?: (coord: AxialCoord) => void; // Called when non-adjacent hex is clicked
}

export const useHexInteraction = ({
  map,
  center,
  zoom,
  isDragging,
  hasDragged,
  offset,
  canvasSize,
  hexSize,
  onCenterChange,
  setZoom,
  hasUnresolvedMonster = false,
  isInCombat = false,
  onPlayerMarkerClick,
  onDistantHexClick,
}: UseHexInteractionProps) => {
  const { player, position: playerPosition, startTravel, isTraveling } = usePlayer();

  const handleClick = useCallback((
    e: React.MouseEvent<HTMLCanvasElement>,
    canvasRef: React.RefObject<HTMLCanvasElement | null>
  ) => {
    // Prevent click if we were dragging or just finished dragging
    if (isDragging || hasDragged) {
      return;
    }

    if (!onCenterChange) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    
    // Calculate mouse position in canvas coordinates
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // Convert to map coordinates
    const centerPixel = hexToPixel(center, hexSize);
    const panX = offset.x / zoom;
    const panY = offset.y / zoom;
    
    // Account for canvas center translation and zoom
    const x = (mouseX - canvasSize.width / 2) / zoom - panX + centerPixel.x;
    const y = (mouseY - canvasSize.height / 2) / zoom - panY + centerPixel.y;
    
    // Convert pixel to hex
    const clickedHex = pixelToHex({ x, y }, hexSize);
    
    // Check if clicked hex is in bounds
    if (!isInBounds(clickedHex, map.width, map.height)) {
      return;
    }

    // Get hex data
    const hexData = map.hexes.get(`${clickedHex.q},${clickedHex.r}`);
    if (!hexData) {
      return;
    }

    // Don't interact with ocean hexes
    if (hexData.terrain === TerrainType.Ocean) {
      return;
    }

    // Calculate distance to player
    const distanceToPlayer = hexDistance(clickedHex, playerPosition);

    // Check if this is an adjacent hex (valid move)
    if (distanceToPlayer === 1 && player.stamina > 0 && !hasUnresolvedMonster && !isInCombat && !isTraveling()) {
      // SYNC FIX: Use onDistantHexClick for adjacent hexes too, so sync validation happens
      // This ensures both isometric map and mini map go through the same validation logic
      console.log(`Clicked adjacent hex (${clickedHex.q}, ${clickedHex.r}) - routing through sync handler`);
      if (onDistantHexClick) {
        onDistantHexClick(clickedHex);
      } else {
        // Fallback: direct travel (old behavior, no sync validation)
        console.log(`⚠️ No sync handler - using direct travel (no validation)`);
        const travelTimeMs = FEATURES.TRAVEL_TIMERS ? getTravelTimeSetting() : 0;
        const route = [playerPosition, clickedHex];
        startTravel(route, travelTimeMs);
      }
      // Center on new position
      onCenterChange(clickedHex);
    } else if (clickedHex.q === playerPosition.q && clickedHex.r === playerPosition.r) {
      // Clicked on current player hex - navigate to action screen
      console.log('Player marker clicked - navigating to action screen');
      if (onPlayerMarkerClick) {
        onPlayerMarkerClick();
      } else {
        // Fallback: just center if no callback provided
        onCenterChange(clickedHex);
      }
    } else {
      // Clicked on a non-adjacent hex
      console.log(`Clicked distant hex (${clickedHex.q}, ${clickedHex.r}), distance: ${distanceToPlayer}`);
      
      // If handler provided for distant hex clicks, use it (for pathfinding modal)
      if (onDistantHexClick && distanceToPlayer > 1 && !hasUnresolvedMonster && !isInCombat && !isTraveling()) {
        onDistantHexClick(clickedHex);
      } else {
        // Otherwise just center the view on it
        onCenterChange(clickedHex);
      }
    }
  }, [
    isDragging,
    hasDragged,
    onCenterChange,
    map,
    center,
    zoom,
    offset,
    canvasSize,
    hexSize,
    player.stamina,
    playerPosition,
    startTravel,
    isTraveling,
    hasUnresolvedMonster,
    isInCombat,
    onPlayerMarkerClick,
    onDistantHexClick,
  ]);

  return {
    handleClick,
  };
};
